'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Is a Moccamaster Plastic-Free? Every Part That Touches Your Coffee (2026)"
      author="MicroPlastics Research Desk"
      date="August 4, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/best-plastic-free-coffee-makers.webp"
      heroImageAlt="Is a Moccamaster plastic free: every component in the Technivorm water path audited, from the cold reservoir to the copper heating element and brew basket"
    >
      <BlogLastUpdated iso="2026-08-04" formatted="August 4, 2026" />

      <BlogTLDR>
        <strong>No. But it puts its plastic in the places that matter
        least.</strong>{' '}
        No drip coffee maker is plastic-free, and anyone selling you one is
        selling you something else. What separates the Moccamaster is{' '}
        <em>where</em> the plastic sits. The reservoir is plastic, but it holds{' '}
        <strong>cold</strong> water. Heating happens inside a{' '}
        <strong>copper element</strong>, not a plastic tank or plastic tubing.
        That matters because <strong>heat is the single biggest multiplier of
        microplastic release</strong>, and the Moccamaster keeps the hot stage
        out of plastic almost entirely. Hot water does touch plastic briefly at
        the outlet arm and brew basket, which Technivorm states are{' '}
        &ldquo;constructed of BPA, BPS, BPF, &amp; phthalate-free plastics.&rdquo;
        The honest gap:{' '}
        <strong>Technivorm never says which polymer it actually uses</strong>,
        and &ldquo;BPA-free&rdquo; describes what is absent, not what is present.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>No drip brewer is plastic-free. The useful question is not <em>whether</em> there is plastic but <strong>whether hot water sits in it</strong>.</>,
          <>The Moccamaster reservoir is plastic but holds <strong>cold water</strong>, which is the low-risk condition. Release is driven far more by heat and wear than by contact alone.</>,
          <>Water is heated in a <strong>copper boiling element</strong> to 196&ndash;205&deg;F, per Technivorm. There is no plastic hot tank and no long plastic hot line, which is the core design difference from a pod machine.</>,
          <>Hot water does contact the <strong>outlet arm and brew basket</strong>. Technivorm states these are &ldquo;BPA, BPS, BPF, &amp; phthalate-free.&rdquo;</>,
          <><strong>Technivorm does not disclose the polymer</strong> in any material we could find on its own site. That is a real limitation and we are not going to fill the gap with a guess.</>,
          <>The <strong>thermal carafe is glass-lined stainless steel</strong> with a borosilicate lining, so the coffee rests in glass, not plastic.</>,
          <>Biggest single upgrade for most people: <strong>a paper filter and a glass or steel carafe</strong>, whatever brewer you own.</>,
        ]}
      />

      <InlineGuessReveal
        source="moccamaster_heating_material"
        question="Inside a Moccamaster, what is the water actually heated in?"
        options={[
          { label: 'A plastic tank' },
          { label: 'Stainless steel' },
          { label: 'A copper element', correct: true },
        ]}
        answer="a copper boiling element"
        answerNote={
          <>
            This is the whole reason the machine scores well. The reservoir is
            plastic, but it holds <em>cold</em> water, and heat is the biggest
            multiplier of release. A pod machine does the opposite: it holds and
            pushes hot water through plastic the entire way.
          </>
        }
        ctaText="Most brewers heat water inside plastic. Scan yours to see which kind you own."
        ctaLabel="Scan your brewer"
      />

      <RiskQuizInlineNudge source="blog_inline_moccamaster" />

      <KeyStats
        heading="The Moccamaster water path, component by component"
        stats={[
          { value: 'Copper', label: 'where the water is actually heated', context: 'not a plastic tank; 196-205°F per Technivorm', source: 'Technivorm Moccamaster (US)', sourceUrl: 'https://us.moccamaster.com/products/kbt' },
          { value: 'Cold', label: 'the water in the plastic reservoir', context: 'heat is the main multiplier of release, and this stage has none', source: 'Technivorm product documentation', sourceUrl: 'https://us.moccamaster.com/' },
          { value: 'Borosilicate', label: 'lining of the stainless thermal carafe', context: 'brewed coffee rests against glass rather than plastic', source: 'Technivorm Moccamaster (US)', sourceUrl: 'https://us.moccamaster.com/products/kbt' },
          { value: 'Undisclosed', label: 'the actual polymer used', context: 'Technivorm states BPA/BPS/BPF/phthalate-free but never names the plastic', source: 'Technivorm brew basket listing', sourceUrl: 'https://us.moccamaster.com/products/brew-basket-for-manual-drip-stop' },
        ]}
      />

      <h2>The question people are actually asking</h2>
      <p>
        Most people searching this have just read something about their Keurig
        and want to know whether the expensive Dutch machine fixes it. The answer
        is: partly, and for a specific reason that is worth understanding,
        because it will make you better at judging every other brewer too.
      </p>
      <p>
        Plastic on its own is not the variable that matters most. What matters is{' '}
        <strong>hot water sitting in plastic</strong>, and secondarily plastic
        that is scratched, aged or under mechanical stress. A plastic tank that
        holds room-temperature water for ten minutes is a very different
        proposition from a plastic tank held at 200&deg;F all day.
      </p>
      <p>
        That single distinction is the whole Moccamaster answer.
      </p>

      <h2>Component by component</h2>

      <BlogComparisonTable
        caption="Every Moccamaster component in the water path, and whether hot water touches it"
        columns={[
          { key: 'part', label: 'Component' },
          { key: 'material', label: 'Material' },
          { key: 'water', label: 'Water state' },
          { key: 'risk', label: 'Assessment' },
        ]}
        rows={[
          { part: 'Water reservoir', material: 'Plastic (polymer not disclosed)', water: 'Cold', risk: 'Low. No heat, short dwell time.' },
          { part: 'Boiling element', material: 'Copper', water: 'Heated to 196-205°F', risk: 'None. This is the key design win.' },
          { part: 'Outlet arm', material: 'Plastic (polymer not disclosed)', water: 'Hot, brief contact', risk: 'The main plastic contact point.' },
          { part: 'Brew basket', material: 'Plastic, stated BPA/BPS/BPF/phthalate-free', water: 'Hot, brief contact', risk: 'Second contact point. Paper filter sits between plastic and grounds.' },
          { part: 'Glass carafe (KBGV etc.)', material: 'Glass', water: 'Hot, long dwell', risk: 'None.' },
          { part: 'Thermal carafe (KBT etc.)', material: 'Glass-lined stainless, borosilicate lining', water: 'Hot, long dwell', risk: 'None for the body. Brew-through lid is plastic.' },
          { part: 'Housing', material: 'Powder-coated aluminium and metal', water: 'No contact', risk: 'Not relevant.' },
        ]}
      />

      <p>
        Read down the &ldquo;water state&rdquo; column and the picture is clear.
        The two stages where water is hottest and sits longest, the boiling
        element and the carafe, are <strong>copper and glass</strong>. The
        plastic parts are either cold (reservoir) or in contact for seconds
        (outlet arm, brew basket).
      </p>

      <h2>What Technivorm will not tell you</h2>
      <p>
        Here is where we have to be straight with you, because it is the part
        most reviews skip.
      </p>
      <p>
        Technivorm states clearly that its plastic parts are free of BPA, BPS,
        BPF and phthalates. The brew basket listing on its own US store says the
        part is &ldquo;Constructed of BPA, BPS, BPF, &amp; phthalate-free
        plastics.&rdquo; That is a genuinely good disclosure by the standards of
        this industry, and it rules out the bisphenol family that people most
        often worry about.
      </p>
      <p>
        But it is a statement about <strong>what is absent</strong>. Across
        Technivorm&apos;s own product pages, spare-part listings and support
        material, we could not find anywhere it names <em>which</em> polymer the
        reservoir, outlet arm or brew basket are made from. Copolyester?
        Polypropylene? Something else? Unstated.
      </p>
      <p>
        We are not going to fill that in with a guess, and you should distrust
        any page that does without citing Technivorm. What we can say is that the
        polymer is undisclosed, and that this limits how precisely anyone can
        assess it. &ldquo;Liner chemistry undisclosed&rdquo; is a true and
        defensible answer, and often the more telling one.
      </p>

      <h2>Moccamaster vs the machine you are replacing</h2>

      <BlogComparisonTable
        caption="Where hot water meets plastic, by brewer type"
        columns={[
          { key: 'brewer', label: 'Brewer' },
          { key: 'hot', label: 'Hot water in plastic?' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { brewer: 'Moccamaster (Technivorm)', hot: 'Briefly, at outlet arm and brew basket', verdict: 'Among the best mainstream electric options' },
          { brewer: 'Pod machines (Keurig, Nespresso)', hot: 'Yes: heated internal lines, and the pod itself is plastic or lined', verdict: 'The case for switching' },
          { brewer: 'Most budget drip makers', hot: 'Yes: plastic hot tank and plastic hot-water path', verdict: 'Worse than Moccamaster on this axis' },
          { brewer: 'Pour-over (glass or ceramic cone) + kettle', hot: 'No, if the cone and kettle are glass, ceramic or steel', verdict: 'The genuinely lowest-plastic route, and cheap' },
          { brewer: 'French press (glass and steel)', hot: 'No', verdict: 'Very low, but check the lid and plunger seal' },
        ]}
      />

      <p>
        If your goal is strictly the least plastic contact for the least money,{' '}
        <strong>a glass or ceramic pour-over cone and a stainless kettle beats
        the Moccamaster</strong> and costs a fraction as much. The Moccamaster
        wins when you want an automatic machine, and among automatic machines its
        architecture is unusually good. That is the honest framing: it is the
        best-designed compromise, not an escape from the compromise.
      </p>

      <h2>What to do</h2>
      <ul>
        <li>
          <strong>Use paper filters.</strong> A paper filter sits between the
          plastic basket and your grounds and also catches a meaningful share of
          particulates. Cheap, and it applies to any brewer.
        </li>
        <li>
          <strong>Pick the thermal or glass carafe, and skip warming plates.</strong>{' '}
          Coffee sitting hot in glass or a borosilicate-lined flask is better
          than coffee sitting hot in anything plastic.
        </li>
        <li>
          <strong>Do not leave water standing in the reservoir.</strong> The
          reservoir is the one plastic part with long dwell time. Fill it when
          you brew rather than the night before, and the cold-water advantage
          stays an advantage.
        </li>
        <li>
          <strong>Replace scratched parts.</strong> Wear matters more than age.
          If the brew basket or reservoir is visibly scuffed or crazed, replace
          it; Technivorm sells the parts individually, which is one of the better
          things about the machine.
        </li>
        <li>
          <strong>Descale with the manufacturer&apos;s method.</strong>{' '}
          Technivorm warns that unsuitable water chemistry leaches minerals from
          the copper element and can damage it permanently. Protecting the copper
          protects the plastic-free part of the water path.
        </li>
      </ul>

      <p>
        For the wider comparison see our{' '}
        <Link href="/blog/best-plastic-free-coffee-makers" className="text-forest underline">best plastic-free coffee makers</Link>{' '}
        and{' '}
        <Link href="/blog/plastic-free-coffee-brewers-ranked" className="text-forest underline">plastic-free coffee brewers ranked</Link>,
        the machine most people are switching away from in{' '}
        <Link href="/blog/do-keurig-coffee-makers-have-microplastics" className="text-forest underline">do Keurig coffee makers have microplastics</Link>,
        and the method-level picture in{' '}
        <Link href="/blog/microplastics-in-coffee-by-brewing-method" className="text-forest underline">microplastics in coffee by brewing method</Link>.
      </p>

      <BlogAppCTA
        headline="Check the brewer on your counter"
        sub="Scan your coffee maker, carafe and filters with MicroPlastics to see which parts contact hot water and what a lower-exposure setup looks like."
      />

      <BlogFAQ
        items={[
          {
            question: 'Is the Moccamaster plastic-free?',
            answer: 'No, and no drip coffee maker is. The Moccamaster reservoir, outlet arm and brew basket are plastic. What makes it better than most is placement: the reservoir holds cold water, and the water is heated inside a copper boiling element rather than a plastic tank, so hot water only touches plastic briefly on its way to the grounds. The carafe is glass or glass-lined stainless steel.',
          },
          {
            question: 'What plastic does Technivorm use in the Moccamaster?',
            answer: 'Technivorm does not say. It states that its plastic parts are free of BPA, BPS, BPF and phthalates, and the brew basket listing on its own store describes the part as "constructed of BPA, BPS, BPF, & phthalate-free plastics." But we could not find anywhere on Technivorm material where the specific polymer is named. That is a claim about what is absent, not what is present, and any page telling you the exact plastic without citing Technivorm is guessing.',
          },
          {
            question: 'Is a Moccamaster better than a Keurig for microplastics?',
            answer: 'On this specific axis, yes. Pod machines heat and hold water in internal plastic lines and then push it through a plastic or plastic-lined pod, so hot water contacts plastic throughout. The Moccamaster heats in copper and only contacts plastic briefly at the outlet arm and brew basket. Whether that difference is worth the price gap depends on how much you value the rest of the machine.',
          },
          {
            question: 'What is the lowest-plastic way to make coffee?',
            answer: 'A glass or ceramic pour-over cone with a paper filter and a stainless steel kettle, poured into a glass or steel vessel. Nothing in that path is heated plastic, and it costs far less than any electric brewer. The Moccamaster is the best compromise if you want an automatic machine, not an alternative to this setup.',
          },
          {
            question: 'Does the Moccamaster water reservoir leach microplastics?',
            answer: 'It holds cold water, which is the low-risk condition, because heat is the largest multiplier of release from plastic. The practical advice is to avoid leaving water standing in it for long periods and to replace the reservoir if it becomes visibly scratched or crazed, since physical wear matters more than age. Technivorm sells reservoirs as individual spare parts.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Technivorm Moccamaster', year: 2026, title: 'Brew basket product listing ("Constructed of BPA, BPS, BPF, & phthalate-free plastics")', publisher: 'us.moccamaster.com', url: 'https://us.moccamaster.com/products/brew-basket-for-manual-drip-stop' },
          { authors: 'Technivorm Moccamaster', year: 2026, title: 'KBT brewer specifications (copper boiling element, 196-205°F, borosilicate glass-lined stainless carafe)', publisher: 'us.moccamaster.com', url: 'https://us.moccamaster.com/products/kbt' },
          { authors: 'European Food Safety Authority', year: 2025, title: 'Review of microplastic release from food contact materials (release driven primarily by physical wear and heat; measured levels very low)', publisher: 'EFSA', url: 'https://www.efsa.europa.eu/en/topics/topic/microplastics-and-nanoplastics' },
        ]}
      />
    </BlogPostLayout>
  )
}
